import { normalizeLocale, type SupportedLocale } from './config';

function labels(
  title: string,
  description: string,
  approve: string,
  deny: string,
  details: string,
  sending: string,
  expired: string,
  error: string,
  unavailable: string,
  empty: string,
  shell: string,
  operation: string,
  extendedWarning: string,
  extendedDescription: string,
  extendedApprove: string,
  extendedCancel: string
) {
  return {
    title,
    description,
    approve,
    deny,
    details,
    sending,
    expired,
    error,
    unavailable,
    empty,
    shell,
    operation,
    extendedWarning,
    extendedDescription,
    extendedApprove,
    extendedCancel,
  };
}
const copy: Record<SupportedLocale, ReturnType<typeof labels>> = {
  en: labels(
    'Allow this action on your computer?',
    'Runs locally on this computer. Approval applies only to this request.',
    'Approve once',
    'Deny',
    'Request details',
    'Sending decision…',
    'This request has expired.',
    'Unable to save your decision. Try again.',
    'This computer cannot run this request right now.',
    'No requests need approval.',
    'Shell',
    'Action',
    'Extended permissions required',
    'Runs outside the folder sandbox. It can access files available to your Mac account beyond the selected folder and use the network. Approval applies only to this exact command, once.',
    'Allow once with extended access',
    'Cancel'
  ),
  ar: labels(
    'هل تسمح بهذا الإجراء على جهازك؟',
    'يُنفَّذ محليًا على هذا الجهاز. تسري الموافقة على هذا الطلب فقط.',
    'موافقة لمرة واحدة',
    'رفض',
    'تفاصيل الطلب',
    'جارٍ إرسال القرار…',
    'انتهت صلاحية هذا الطلب.',
    'تعذر حفظ قرارك. حاول مجددًا.',
    'لا يمكن لهذا الجهاز تنفيذ الطلب الآن.',
    'لا توجد طلبات تحتاج إلى موافقة.',
    'الصدفة',
    'الإجراء',
    'مطلوب أذونات موسعة',
    'يُنفَّذ خارج بيئة عزل المجلد. يمكنه الوصول إلى الملفات المتاحة لحسابك على Mac خارج المجلد المحدد واستخدام الشبكة. تسري الموافقة على هذا الأمر المحدد فقط، لمرة واحدة.',
    'السماح لمرة واحدة بوصول موسع',
    'إلغاء'
  ),
  es: labels(
    '¿Permitir esta acción en tu equipo?',
    'Se ejecuta localmente en este equipo. La aprobación solo se aplica a esta solicitud.',
    'Aprobar una vez',
    'Denegar',
    'Detalles de la solicitud',
    'Enviando decisión…',
    'Esta solicitud ha caducado.',
    'No se pudo guardar tu decisión. Inténtalo de nuevo.',
    'Este equipo no puede ejecutar la solicitud ahora.',
    'No hay solicitudes pendientes de aprobación.',
    'Intérprete',
    'Acción',
    'Se requieren permisos ampliados',
    'Se ejecuta fuera del aislamiento de la carpeta. Puede acceder a archivos disponibles para tu cuenta de Mac fuera de la carpeta seleccionada y usar la red. La aprobación se aplica solo a este comando exacto, una vez.',
    'Permitir una vez con acceso ampliado',
    'Cancelar'
  ),
  'pt-BR': labels(
    'Permitir esta ação no seu computador?',
    'Executa localmente neste computador. A aprovação vale apenas para esta solicitação.',
    'Aprovar uma vez',
    'Negar',
    'Detalhes da solicitação',
    'Enviando decisão…',
    'Esta solicitação expirou.',
    'Não foi possível salvar sua decisão. Tente novamente.',
    'Este computador não pode executar a solicitação agora.',
    'Nenhuma solicitação precisa de aprovação.',
    'Shell',
    'Ação',
    'Permissões ampliadas necessárias',
    'Executa fora do isolamento da pasta. Pode acessar arquivos disponíveis para sua conta do Mac além da pasta selecionada e usar a rede. A aprovação vale apenas para este comando exato, uma vez.',
    'Permitir uma vez com acesso ampliado',
    'Cancelar'
  ),
  fr: labels(
    'Autoriser cette action sur votre ordinateur ?',
    'S’exécute localement sur cet ordinateur. L’autorisation s’applique uniquement à cette demande.',
    'Autoriser une fois',
    'Refuser',
    'Détails de la demande',
    'Envoi de la décision…',
    'Cette demande a expiré.',
    'Impossible d’enregistrer votre décision. Réessayez.',
    'Cet ordinateur ne peut pas exécuter cette demande pour le moment.',
    'Aucune demande ne nécessite d’autorisation.',
    'Interpréteur',
    'Action',
    'Autorisations étendues requises',
    'S’exécute hors du bac à sable du dossier. Peut accéder aux fichiers disponibles pour votre compte Mac au-delà du dossier sélectionné et utiliser le réseau. L’autorisation s’applique une seule fois à cette commande exacte.',
    'Autoriser une fois avec un accès étendu',
    'Annuler'
  ),
  it: labels(
    'Consentire questa azione sul computer?',
    'Viene eseguita localmente su questo computer. L’approvazione vale solo per questa richiesta.',
    'Approva una volta',
    'Rifiuta',
    'Dettagli della richiesta',
    'Invio della decisione…',
    'Questa richiesta è scaduta.',
    'Impossibile salvare la decisione. Riprova.',
    'Questo computer non può eseguire la richiesta al momento.',
    'Nessuna richiesta necessita di approvazione.',
    'Shell',
    'Azione',
    'Sono necessarie autorizzazioni estese',
    'Viene eseguito fuori dalla sandbox della cartella. Può accedere ai file disponibili al tuo account Mac oltre la cartella selezionata e usare la rete. L’approvazione vale solo per questo comando esatto, una volta.',
    'Consenti una volta con accesso esteso',
    'Annulla'
  ),
  ko: labels(
    '이 컴퓨터에서 이 작업을 허용할까요?',
    '이 컴퓨터에서 로컬로 실행됩니다. 승인은 이 요청에만 적용됩니다.',
    '한 번 승인',
    '거부',
    '요청 세부 정보',
    '결정 전송 중…',
    '이 요청은 만료되었습니다.',
    '결정을 저장할 수 없습니다. 다시 시도하세요.',
    '지금은 이 컴퓨터에서 요청을 실행할 수 없습니다.',
    '승인이 필요한 요청이 없습니다.',
    '셸',
    '작업',
    '확장 권한 필요',
    '폴더 샌드박스 밖에서 실행됩니다. 선택한 폴더 외부에서 Mac 계정으로 접근 가능한 파일에 접근하고 네트워크를 사용할 수 있습니다. 승인은 이 정확한 명령에 한 번만 적용됩니다.',
    '확장 접근으로 한 번 허용',
    '취소'
  ),
  de: labels(
    'Diese Aktion auf deinem Computer zulassen?',
    'Wird lokal auf diesem Computer ausgeführt. Die Genehmigung gilt nur für diese Anfrage.',
    'Einmal genehmigen',
    'Ablehnen',
    'Details zur Anfrage',
    'Entscheidung wird gesendet…',
    'Diese Anfrage ist abgelaufen.',
    'Deine Entscheidung konnte nicht gespeichert werden. Versuche es erneut.',
    'Dieser Computer kann die Anfrage gerade nicht ausführen.',
    'Keine Anfragen erfordern eine Genehmigung.',
    'Shell',
    'Aktion',
    'Erweiterte Berechtigungen erforderlich',
    'Wird außerhalb der Ordner-Sandbox ausgeführt. Kann auf Dateien zugreifen, die deinem Mac-Konto außerhalb des ausgewählten Ordners zugänglich sind, und das Netzwerk nutzen. Die Genehmigung gilt nur einmal für genau diesen Befehl.',
    'Einmal mit erweitertem Zugriff erlauben',
    'Abbrechen'
  ),
  ja: labels(
    'このコンピューターでの操作を許可しますか？',
    'このコンピューター上で実行されます。承認はこのリクエストにのみ適用されます。',
    '今回のみ承認',
    '拒否',
    'リクエストの詳細',
    '判断を送信中…',
    'このリクエストは期限切れです。',
    '判断を保存できませんでした。もう一度お試しください。',
    '現在、このコンピューターではリクエストを実行できません。',
    '承認が必要なリクエストはありません。',
    'シェル',
    '操作',
    '拡張権限が必要です',
    'フォルダーのサンドボックス外で実行されます。選択したフォルダー以外でもMacアカウントでアクセスできるファイルにアクセスし、ネットワークを使用できます。承認はこの正確なコマンドに1回のみ適用されます。',
    '拡張アクセスで今回のみ許可',
    'キャンセル'
  ),
  hi: labels(
    'इस कंप्यूटर पर यह कार्रवाई अनुमति दें?',
    'यह इस कंप्यूटर पर स्थानीय रूप से चलता है। स्वीकृति केवल इस अनुरोध के लिए है।',
    'एक बार अनुमति दें',
    'अस्वीकार करें',
    'अनुरोध विवरण',
    'निर्णय भेजा जा रहा है…',
    'इस अनुरोध की समय सीमा समाप्त हो गई है।',
    'आपका निर्णय सहेजा नहीं जा सका। फिर कोशिश करें।',
    'यह कंप्यूटर अभी यह अनुरोध नहीं चला सकता।',
    'किसी अनुरोध को स्वीकृति नहीं चाहिए।',
    'शेल',
    'कार्रवाई',
    'विस्तारित अनुमतियाँ आवश्यक हैं',
    'फ़ोल्डर सैंडबॉक्स के बाहर चलता है। यह चुने गए फ़ोल्डर से बाहर आपके Mac खाते के लिए उपलब्ध फ़ाइलों तक पहुँच सकता है और नेटवर्क का उपयोग कर सकता है। स्वीकृति केवल इसी सटीक कमांड के लिए एक बार लागू होती है।',
    'विस्तारित पहुँच के साथ एक बार अनुमति दें',
    'रद्द करें'
  ),
  ru: labels(
    'Разрешить это действие на вашем компьютере?',
    'Выполняется локально на этом компьютере. Разрешение относится только к этому запросу.',
    'Разрешить один раз',
    'Отклонить',
    'Сведения о запросе',
    'Отправка решения…',
    'Срок действия запроса истёк.',
    'Не удалось сохранить решение. Повторите попытку.',
    'Сейчас этот компьютер не может выполнить запрос.',
    'Нет запросов, требующих разрешения.',
    'Оболочка',
    'Действие',
    'Требуются расширенные разрешения',
    'Выполняется вне изолированной среды папки. Может обращаться к файлам, доступным вашей учётной записи Mac за пределами выбранной папки, и использовать сеть. Разрешение действует один раз только для этой точной команды.',
    'Разрешить один раз с расширенным доступом',
    'Отмена'
  ),
  zh: labels(
    '允许在此电脑上执行此操作吗？',
    '将在此电脑上本地运行。批准仅适用于此请求。',
    '批准一次',
    '拒绝',
    '请求详情',
    '正在发送决定…',
    '此请求已过期。',
    '无法保存你的决定。请重试。',
    '此电脑目前无法执行此请求。',
    '没有需要批准的请求。',
    '命令解释器',
    '操作',
    '需要扩展权限',
    '将在文件夹沙盒外运行。可访问所选文件夹之外你的 Mac 账户可访问的文件，并使用网络。批准仅对这条确切的命令生效一次。',
    '允许一次扩展访问',
    '取消'
  ),
  'zh-TW': labels(
    '允許在此電腦上執行此操作嗎？',
    '將在此電腦上本機執行。核准僅適用於此請求。',
    '核准一次',
    '拒絕',
    '請求詳細資料',
    '正在傳送決定…',
    '此請求已過期。',
    '無法儲存你的決定。請重試。',
    '此電腦目前無法執行此請求。',
    '沒有需要核准的請求。',
    '命令殼層',
    '操作',
    '需要擴充權限',
    '將在資料夾沙盒外執行。可存取所選資料夾以外你的 Mac 帳戶可存取的檔案，並使用網路。核准僅對這條確切的命令生效一次。',
    '允許一次擴充存取',
    '取消'
  ),
  vi: labels(
    'Cho phép thao tác này trên máy tính của bạn?',
    'Chạy cục bộ trên máy tính này. Phê duyệt chỉ áp dụng cho yêu cầu này.',
    'Phê duyệt một lần',
    'Từ chối',
    'Chi tiết yêu cầu',
    'Đang gửi quyết định…',
    'Yêu cầu này đã hết hạn.',
    'Không thể lưu quyết định. Hãy thử lại.',
    'Máy tính này hiện không thể thực hiện yêu cầu.',
    'Không có yêu cầu cần phê duyệt.',
    'Trình lệnh',
    'Thao tác',
    'Cần quyền mở rộng',
    'Chạy ngoài môi trường cách ly thư mục. Có thể truy cập các tệp mà tài khoản Mac của bạn có quyền truy cập ngoài thư mục đã chọn và sử dụng mạng. Phê duyệt chỉ áp dụng một lần cho đúng lệnh này.',
    'Cho phép một lần với quyền truy cập mở rộng',
    'Hủy'
  ),
  id: labels(
    'Izinkan tindakan ini di komputer Anda?',
    'Berjalan secara lokal di komputer ini. Persetujuan hanya berlaku untuk permintaan ini.',
    'Setujui sekali',
    'Tolak',
    'Detail permintaan',
    'Mengirim keputusan…',
    'Permintaan ini sudah kedaluwarsa.',
    'Keputusan tidak dapat disimpan. Coba lagi.',
    'Komputer ini belum dapat menjalankan permintaan tersebut.',
    'Tidak ada permintaan yang memerlukan persetujuan.',
    'Shell',
    'Tindakan',
    'Izin tambahan diperlukan',
    'Berjalan di luar sandbox folder. Dapat mengakses file yang tersedia untuk akun Mac Anda di luar folder yang dipilih dan menggunakan jaringan. Persetujuan hanya berlaku sekali untuk perintah persis ini.',
    'Izinkan sekali dengan akses tambahan',
    'Batal'
  ),
};
export function getDesktopCommandApprovalCopy(locale: string) {
  return copy[normalizeLocale(locale)];
}
